<script setup lang="ts">
import WalletPreview from '@/views/loyaltySupports/wallet/components/WalletPreview.vue'

import FormColorPicker from '@/components/ui/ColorPicker/ColorPicker.vue'

import type { CodeType, Wallet, WalletErrors } from '@/types/wallet'
import type { ImageFile } from '@/types/types'

import { syncWalletDerivedFields } from '../walletFields'

const props = defineProps<{
  wallet: Wallet
  errors: WalletErrors
  editMode: boolean
}>()

const { t } = useI18n()
const { wallet } = toRefs(props)

const logoImages = ref<ImageFile[]>([])
const logoInitialImages = ref<ImageFile[]>([])
const logoDeletedImages = ref<number[]>([])

const coverImages = ref<ImageFile[]>([])
const coverInitialImages = ref<ImageFile[]>([])
const coverDeletedImages = ref<number[]>([])

const syncInitialImages = () => {
  logoInitialImages.value = wallet.value.fields['iosLogo']
    ? [{ id: 1, path: wallet.value.fields['iosLogo'], name: 'ios-logo' }]
    : []
  coverInitialImages.value = wallet.value.fields['iosImage']
    ? [{ id: 2, path: wallet.value.fields['iosImage'], name: 'ios-cover' }]
    : []
}

onMounted(syncInitialImages)

const codeTypes: CodeType[] = [
  {
    id: 3,
    name: 'qrcode',
    title: t('pages.loyaltySupports.wallet.config.qrcode'),
    class: 'h-36 w-36 rounded-xl',
    disabled: !props.editMode
  },
  {
    id: 4,
    name: 'barcode',
    title: t('pages.loyaltySupports.wallet.config.barcode'),
    class: 'h-20 w-48 rounded',
    disabled: !props.editMode
  }
]

const codeType = computed({
  get: () => codeTypes.find((type) => type.id === wallet.value.walletModelId),
  set: (newValue) => {
    wallet.value.walletModelId = newValue.id
  }
})

const coverResolution = {
  width: 1032,
  height: 336
}

const updateLogo = (newImages: ImageFile[]) => {
  logoImages.value = newImages
  wallet.value.fields['iosLogo'] = newImages[0]?.path ?? ''
  syncWalletDerivedFields(wallet.value.fields)
}

const updateImage = (newImages: ImageFile[]) => {
  coverImages.value = newImages
  wallet.value.fields['iosImage'] = newImages[0]?.path ?? ''
  syncWalletDerivedFields(wallet.value.fields)
}

const syncPointsTitle = () => {
  syncWalletDerivedFields(wallet.value.fields)
}

const syncTitleColors = () => {
  syncWalletDerivedFields(wallet.value.fields)
}
</script>

<template>
  <div class="mt-8 xl:grid xl:grid-cols-3 xl:gap-4">
    <div class="md:col-span-2">
      <div class="border bg-white sm:rounded-md">
        <div class="border-b px-8 py-8">
          <h3 class="text-lg font-medium leading-6 text-foreground">
            {{ t('pages.loyaltySupports.wallet.config.title') }}
          </h3>
          <p class="mt-1 text-sm text-muted-foreground">
            {{ t('pages.loyaltySupports.wallet.config.subtitle') }}
          </p>
        </div>
        <div class="grid gap-y-6 px-2 py-5 sm:mt-6 sm:px-8 sm:pb-8">
          <div>
            <label class="mb-2 block text-sm font-medium text-foreground">
              {{ t('pages.loyaltySupports.wallet.config.logo') }}
            </label>
            <ImageUploaderNew
              :images="logoImages"
              :initial-images="logoInitialImages"
              :deleted-images="logoDeletedImages"
              single-image
              :aspect-ratio="[1, 1]"
              :disabled="!editMode"
              @update:images="updateLogo"
              @update:deleted-images="logoDeletedImages = $event"
            />
          </div>
          <div>
            <label class="mb-2 block text-sm font-medium text-foreground">
              {{ t('pages.loyaltySupports.wallet.config.cover.label') }}
            </label>
            <p class="mb-2 text-xs text-muted-foreground">
              {{
                t('pages.loyaltySupports.wallet.config.cover.tip', {
                  resolution: `${coverResolution.width}x${coverResolution.height}`
                })
              }}
            </p>
            <ImageUploaderNew
              :images="coverImages"
              :initial-images="coverInitialImages"
              :deleted-images="coverDeletedImages"
              single-image
              :aspect-ratio="[coverResolution.width, coverResolution.height]"
              :disabled="!editMode"
              @update:images="updateImage"
              @update:deleted-images="coverDeletedImages = $event"
            />
          </div>

          <div>
            <label class="mb-1 inline-block text-sm font-medium text-foreground">
              {{ t('pages.loyaltySupports.wallet.config.codeType') }}
            </label>
            <AppRadioCard v-model="codeType" :options="codeTypes" class="grid grid-cols-2 gap-4" />
          </div>
          <div class="border-t pt-6 sm:mt-2 sm:grid sm:grid-cols-2 sm:gap-x-4">
            <div>
              <label for="backgroundColor" class="mb-1 inline-block text-sm font-medium text-foreground">
                {{ t('pages.loyaltySupports.wallet.config.backgroundColor') }}
              </label>
              <div class="flex h-12 w-max">
                <FormColorPicker
                  id="backgroundColor"
                  v-model="wallet.fields['backgroundColor']"
                  :disabled="!editMode"
                />
              </div>
            </div>

            <div>
              <label for="textColor" class="mb-1 inline-block text-sm font-medium text-foreground">
                {{ t('pages.loyaltySupports.wallet.config.textColor') }}
              </label>
              <div class="flex h-12 w-max">
                <FormColorPicker
                  id="textColor"
                  v-model="wallet.fields['iosTitleColor']"
                  :disabled="!editMode"
                  @update:model-value="syncTitleColors()"
                />
              </div>
            </div>
          </div>

          <div class="gap-4 sm:grid sm:grid-cols-2">
            <div class="col-span-1">
              <AppFormInput
                id="pointsTitle"
                v-model="wallet.fields['iosPointsTitle']"
                :disabled="!editMode"
                :label="`${t('pages.loyaltySupports.wallet.config.pointsTitle')} *`"
                :errors="errors?.iosPointsTitle"
                type="text"
                name="pointsTitle"
                class="mt-2"
                @update:model-value="syncPointsTitle()"
              />
            </div>
            <div class="col-span-1">
              <AppFormInput
                id="merchantName"
                v-model="wallet.fields['merchantName']"
                :disabled="!editMode"
                :label="`${t('pages.loyaltySupports.wallet.config.passName')} *`"
                :errors="errors?.merchantName"
                type="text"
                name="merchantName"
                class="mt-2"
              />
            </div>
            <div class="col-span-1">
              <AppFormInput
                id="androidMandatoryTitle"
                v-model="wallet.fields['androidMandatoryTitle']"
                :disabled="!editMode"
                :label="`${t('pages.loyaltySupports.wallet.config.androidMandatoryTitle')} *`"
                :errors="errors?.androidMandatoryTitle"
                type="text"
                name="androidMandatoryTitle"
                class="mt-2"
              />
            </div>
          </div>
        </div>
      </div>
    </div>

    <WalletPreview :wallet="wallet" :code-type="codeType" />
  </div>
</template>
